import { useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useCreateAdvantageMutation } from '@/api/endpoints/mainPages';
import { DataSection, InputField, SelectField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';
import { EMainPageAdvantageIcons } from '@/shared/types/mainPage';

import { ADVANTAGE_VALIDATION_SCHEMA } from '../../consts';
import type { IFormData } from '../../types';
import { IconSelectLabel } from '../IconSelectLabel/IconSelectLabel';

import styles from './AdvantageModals.module.scss';

type TProp = {
  pageId: number;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
};

export const CreateAdvantageModal = ({ pageId, isModalOpen, handleCancel }: TProp) => {
  const [createAdvantage, { isLoading: isAdvantageCreating }] = useCreateAdvantageMutation();

  const sendApiRequest = useApiRequest();

  const methods = useForm<IFormData>({
    defaultValues: {
      title: '',
      description: '',
      icon: undefined,
    },
    resolver: yupResolver(ADVANTAGE_VALIDATION_SCHEMA),
  });

  const { handleSubmit } = methods;

  const handleCreateAdvantage = handleSubmit(({ title, description, icon }) => {
    if (icon) {
      sendApiRequest({
        successMessage: 'Преимущество успешно создано',
        apiRequestCallback: () =>
          createAdvantage({
            id: pageId,
            data: {
              title,
              description,
              icon,
            },
          }),
        successCallback: handleCancel,
      });
    }
  });

  const badgeIconsList = useMemo(
    () =>
      Object.values(EMainPageAdvantageIcons).map((iconType) => ({
        value: iconType,
        label: <IconSelectLabel iconType={iconType} />,
      })),
    [],
  );

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <Spin spinning={isAdvantageCreating}>
        <div className={styles.wrapper}>
          <FormProvider {...methods}>
            <p className={styles.title}>Добавление преимущества школы</p>
            <div className={styles['content-wrapper']}>
              <DataSection
                titleText="Заголовок преимущества"
                isTitleRequired
              >
                <InputField
                  fieldName="title"
                  placeholder="Заголовок"
                  shouldEnableLengthCounter
                  maxLengthCount={200}
                />
              </DataSection>
              <DataSection
                titleText="Описание преимущества"
                isTitleRequired
              >
                <InputField
                  fieldName="description"
                  placeholder="Описание"
                  shouldEnableLengthCounter
                  maxLengthCount={500}
                />
              </DataSection>
              <DataSection
                titleText="Иконка преимущества"
                isTitleRequired
              >
                <SelectField
                  fieldName="icon"
                  size="large"
                  virtual={false}
                  showSearch={false}
                  placeholder="Выбрать иконку"
                  options={badgeIconsList}
                />
              </DataSection>
            </div>
            <div className={styles['buttons-wrapper']}>
              <Button
                size="large"
                onClick={handleCancel}
                className={styles.button}
                danger
              >
                Отмена
              </Button>
              <Button
                size="large"
                onClick={handleCreateAdvantage}
                type="primary"
                className={styles.button}
                disabled={isAdvantageCreating}
              >
                Сохранить
              </Button>
            </div>
          </FormProvider>
        </div>
      </Spin>
    </Modal>
  );
};
